import { useEffect, useMemo, useState } from 'react';
import {
  Radio, Plus, Search, Pencil, Ban, CheckCircle2, Loader2, Unlink,
} from 'lucide-react';
import {
  fetchRfidCards, disableRfidCard, updateRfidCard, releaseAutochargeCredential, releaseRfidCard,
  type RfidCardRow,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { RfidCardFormModal } from './RfidCardFormModal';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './RfidCardsPage.module.css';

/**
 * Admin RFID Cards management.
 *
 * Lists cards grouped by driver (drivers with no cards don't appear).
 * Within each driver the cards sort created_at desc. Create opens a
 * modal for tag UID + label + user typeahead. Row click opens an
 * edit drawer; soft-disable / reactivate via destructive confirm
 * modals.
 *
 * Warren uses this to register his Weeyu test tags (2002610,
 * 10000329, 4CA7BCEF) to his own account so Authorize returns
 * Accepted end-to-end.
 */
export function RfidCardsPage() {
  useRecentRoute('Card Management');
  const [cards, setCards] = useState<RfidCardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<RfidCardRow | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    { card: RfidCardRow; kind: 'disable' | 'enable' | 'release' } | null
  >(null);
  const [confirmPending, setConfirmPending] = useState(false);

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchRfidCards({
        ...(activeFilter === 'active' ? { isActive: true } : {}),
        ...(activeFilter === 'inactive' ? { isActive: false } : {}),
      });
      setCards(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cards');
    } finally {
      setLoading(false);
    }
  };

  // `reload` is intentionally not in the dep array: including it
  // would loop (reload is redefined every render), and the only
  // external trigger we need to react to is activeFilter.
  useEffect(() => {
    void reload();
  }, [activeFilter]);

  // Client-side search — the API also supports `q` but the table is
  // small enough that filtering in-memory keeps the UI responsive
  // without a round-trip per keystroke.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => {
      const bag = [
        c.tag_uid,
        c.label ?? '',
        c.user?.email ?? '',
        c.user?.username ?? '',
        c.user?.name ?? '',
      ].join(' ').toLowerCase();
      return bag.includes(q);
    });
  }, [cards, search]);

  // Group cards by user. Orphan cards (user=null) collected under
  // a synthetic "Orphaned" group at the bottom so they're visible
  // for cleanup without breaking the main by-driver view.
  const groups = useMemo(() => groupByDriver(visible), [visible]);

  const handleConfirmAction = async (): Promise<void> => {
    if (!confirmAction) return;
    setConfirmPending(true);
    try {
      if (confirmAction.kind === 'disable') {
        await disableRfidCard(confirmAction.card._id);
      } else if (confirmAction.kind === 'release') {
        if (confirmAction.card.credential_kind === 'vid') {
          await releaseAutochargeCredential(confirmAction.card._id);
        } else {
          await releaseRfidCard(confirmAction.card._id);
        }
      } else {
        await updateRfidCard(confirmAction.card._id, { is_active: true });
      }
      setConfirmAction(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setConfirmPending(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}><Radio size={20} /></span>
          <div>
            <h1 className={styles.title}>RFID Cards</h1>
            <p className={styles.subtitle}>
              Register physical cards to drivers so Authorize returns Accepted end-to-end.
            </p>
          </div>
        </div>
        <button
          type="button"
          className={formStyles.btnPrimary}
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={14} /> Add card
        </button>
      </header>

      <div className={styles.controls}>
        <div className={styles.searchWrap}>
          <Search size={14} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search tag, label, driver email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={styles.filterSelect}
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as typeof activeFilter)}
        >
          <option value="all">All cards</option>
          <option value="active">Active only</option>
          <option value="inactive">Disabled only</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && cards.length === 0 ? (
        <div className={styles.empty}>
          <Loader2 size={18} className={styles.spin} />
          <span>Loading cards…</span>
        </div>
      ) : visible.length === 0 ? (
        <div className={styles.empty}>
          {cards.length === 0
            ? 'No RFID cards registered yet. Add one to authorize a tap.'
            : 'No cards match the current filter.'}
        </div>
      ) : (
        <div className={styles.groupList}>
          {groups.map((g) => (
            <section key={g.key} className={styles.group}>
              <header className={styles.groupHeader}>
                <span className={styles.groupName}>
                  {g.driverLabel}
                </span>
                <span className={styles.groupCount}>
                  {g.cards.length} {g.cards.length === 1 ? 'card' : 'cards'}
                </span>
              </header>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Tag UID</th>
                    <th>Label</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className={styles.colActions} />
                  </tr>
                </thead>
                <tbody>
                  {g.cards.map((c) => (
                    <tr
                      key={c._id}
                      className={`${styles.row} ${!c.is_active ? styles.rowInactive : ''}`}
                      onClick={() => setEditingCard(c)}
                    >
                      <td className={styles.tagCell}>
                        {c.tag_uid}
                        {c.credential_kind === 'vid' && (
                          <span className={styles.kindBadge} title="Autocharge vehicle id — linked by the driver from a session, not a card">vehicle id</span>
                        )}
                      </td>
                      <td>{c.label ?? <span className={styles.dim}>—</span>}</td>
                      <td>
                        {c.is_active
                          ? <span className={styles.badgeOk}><CheckCircle2 size={11} /> Active</span>
                          : <span className={styles.badgeOff}>{c.suspended_by_rule ? 'Paused by rule' : c.pending ? 'Pending' : c.disabled_by_admin ? 'Disabled' : !c.user ? 'Unbound' : c.credential_kind === 'vid' ? 'Unlinked by driver' : 'Disabled'}</span>}
                      </td>
                      <td className={styles.timeCell}>{formatDate(c.created_at)}</td>
                      <td className={styles.colActions} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className={styles.iconBtn}
                          title="Edit card"
                          onClick={() => setEditingCard(c)}
                        >
                          <Pencil size={12} />
                        </button>
                        {c.user && !c.fleet_fob && (
                          <button
                            type="button"
                            className={styles.iconBtn}
                            title={c.credential_kind === 'vid'
                              ? 'Release vehicle id — unbind it (sold car, wrong account) so its real owner can link it'
                              : 'Release fob — unbind it (registered to the wrong account) so the person holding it can register it'}
                            onClick={() => setConfirmAction({ card: c, kind: 'release' })}
                          >
                            <Unlink size={12} />
                          </button>
                        )}
                        {/* Disable is offered on any row EVI has not already
                            disabled - a pending or driver-unlinked credential
                            included, since those are the ones that can come
                            back on by themselves (Warren, 2026-09-26). */}
                        {!c.disabled_by_admin && (
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            title="Disable card"
                            onClick={() => setConfirmAction({ card: c, kind: 'disable' })}
                          >
                            <Ban size={12} />
                          </button>
                        )}
                        {!c.is_active && (
                          <button
                            type="button"
                            className={styles.iconBtn}
                            /* A vehicle id paused by a plug & charge rule comes back when
                               a super_admin lifts the rule, not card by card — the server
                               answers 409 PAUSED_BY_RULE either way. */
                            title={c.suspended_by_rule ? 'Paused by a plug & charge rule — lift the rule to restore' : 'Reactivate card'}
                            disabled={c.suspended_by_rule === true}
                            onClick={() => setConfirmAction({ card: c, kind: 'enable' })}
                          >
                            <CheckCircle2 size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      {createOpen && (
        <RfidCardFormModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={async () => {
            setCreateOpen(false);
            await reload();
          }}
        />
      )}

      {editingCard && (
        <RfidCardFormModal
          mode="edit"
          card={editingCard}
          onClose={() => setEditingCard(null)}
          onSaved={async () => {
            setEditingCard(null);
            await reload();
          }}
        />
      )}

      <Modal
        isOpen={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        title={confirmAction?.kind === 'disable' ? 'Disable card'
          : confirmAction?.kind === 'release'
            ? (confirmAction.card.credential_kind === 'vid' ? 'Release vehicle id' : 'Release fob')
            : 'Reactivate card'}
        size="sm"
      >
        {confirmAction && (
          <div className={styles.confirmBody}>
            <p>
              {confirmAction.kind === 'disable' ? (
                <>
                  Disable card <code>{confirmAction.card.tag_uid}</code>
                  {confirmAction.card.label ? <> ({confirmAction.card.label})</> : null}?
                  {confirmAction.card.credential_kind === 'vid'
                    ? <> The car stops starting sessions and stays off until it's reactivated here:
                      the account can't link it again. Session history stays intact.</>
                    : <> The card stops working and stays off until it's reactivated here: nobody
                      can register it in the meantime. Session history stays intact.</>}
                </>
              ) : confirmAction.kind === 'release' && confirmAction.card.credential_kind !== 'vid' ? (
                <>
                  Release fob <code>{confirmAction.card.tag_uid}</code>
                  {confirmAction.card.label ? <> ({confirmAction.card.label})</> : null} from{' '}
                  {confirmAction.card.user?.name ?? confirmAction.card.user?.email ?? 'its account'}?
                  It stops working on that account, and that account can't register it again. The
                  person holding it can register it from their own account at the charger. Session
                  history stays intact.
                </>
              ) : confirmAction.kind === 'release' ? (
                <>
                  Release vehicle id <code>{confirmAction.card.tag_uid}</code>
                  {confirmAction.card.label ? <> ({confirmAction.card.label})</> : null} from{' '}
                  {confirmAction.card.user?.name ?? confirmAction.card.user?.email ?? 'its account'}?
                  The car stops starting sessions on that account, and that account can't link it
                  again. Whoever plugs it in next can link it from their own charging screen. Session
                  history stays intact.
                </>
              ) : (
                <>
                  Reactivate card <code>{confirmAction.card.tag_uid}</code>
                  {confirmAction.card.label ? <> ({confirmAction.card.label})</> : null}?
                </>
              )}
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setConfirmAction(null)}
                disabled={confirmPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={
                  confirmAction.kind === 'disable' || confirmAction.kind === 'release'
                    ? styles.confirmDanger
                    : formStyles.btnPrimary
                }
                onClick={() => void handleConfirmAction()}
                disabled={confirmPending}
              >
                {confirmPending
                  ? <><Loader2 size={12} className={styles.spin} /> Working…</>
                  : confirmAction.kind === 'disable' ? 'Disable' : confirmAction.kind === 'release' ? 'Release' : 'Reactivate'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Grouping ──────────────────────────────────────────────────────

interface DriverGroup {
  key: string;
  driverLabel: string;
  cards: RfidCardRow[];
}

function groupByDriver(cards: RfidCardRow[]): DriverGroup[] {
  const map = new Map<string, DriverGroup>();
  for (const c of cards) {
    const key = c.user?._id ?? 'orphan';
    const driverLabel = c.user
      ? (c.user.username ?? c.user.email ?? 'Unknown driver')
      : 'Orphaned (user removed)';
    let g = map.get(key);
    if (!g) {
      g = { key, driverLabel, cards: [] };
      map.set(key, g);
    }
    g.cards.push(c);
  }
  const groups = Array.from(map.values());
  // Within each group: created_at desc.
  for (const g of groups) {
    g.cards.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
  // Group order: by most-recent card in the group, desc. Orphan
  // group pinned to the bottom for visual cleanup.
  groups.sort((a, b) => {
    if (a.key === 'orphan') return 1;
    if (b.key === 'orphan') return -1;
    const aLast = a.cards[0]?.created_at ?? '';
    const bLast = b.cards[0]?.created_at ?? '';
    return bLast.localeCompare(aLast);
  });
  return groups;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}
